import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Album, Organization } from '@nargus/shared';
import { Link } from '@tanstack/react-router';
import { Images, LoaderCircle, Pencil, Plus, Trash2 } from 'lucide-react';
import { useBlobUrl } from '@/hooks/use-blob-url';
import { useInView } from '@/hooks/use-in-view';
import {
  useAlbums,
  useCreateAlbum,
  useDeleteAlbum,
  useRenameAlbum,
} from '@/resources/album/album.service';
import { photoThumbnailLoader } from '@/resources/photo/photo.service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PhotoTabs } from './photos-section';
import { ConfirmDialog, EmptyNote, ErrorNote, SectionHeader } from './ui';

export function AlbumsSection({ organization }: { organization: Organization }) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<Album | null>(null);
  const [removing, setRemoving] = useState<Album | null>(null);
  const albums = useAlbums(organization.id);
  const remove = useDeleteAlbum();

  return (
    <div className="flex flex-col gap-4">
      <PhotoTabs organizationId={organization.id} active="albums" />

      <div className="flex items-start justify-between gap-4">
        <SectionHeader title={t('albums.title')} hint={t('albums.hint')} />
        <Button type="button" className="shrink-0" onClick={() => setCreating(true)}>
          <Plus />
          {t('albums.create')}
        </Button>
      </div>

      <ErrorNote error={albums.error} />

      {albums.isPending ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <li key={index}>
              <Skeleton className="aspect-square w-full rounded-lg" />
            </li>
          ))}
        </ul>
      ) : albums.data?.length === 0 ? (
        <EmptyNote>{t('albums.none')}</EmptyNote>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {albums.data?.map((album) => (
            <AlbumCard
              key={album.id}
              album={album}
              organization={organization}
              onRename={() => setRenaming(album)}
              onRemove={() => setRemoving(album)}
            />
          ))}
        </ul>
      )}

      {creating && <AlbumDialog organization={organization} onClose={() => setCreating(false)} />}

      {renaming && (
        <AlbumDialog
          organization={organization}
          album={renaming}
          onClose={() => setRenaming(null)}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={t('albums.removeTitle')}
          description={t('albums.removeHint', { name: removing.name })}
          confirmLabel={t('common.delete')}
          busy={remove.isPending}
          error={remove.error}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            remove.mutate(
              { organizationId: organization.id, albumId: removing.id },
              { onSuccess: () => setRemoving(null) },
            )
          }
        />
      )}
    </div>
  );
}

function AlbumCard({
  album,
  organization,
  onRename,
  onRemove,
}: {
  album: Album;
  organization: Organization;
  onRename: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { ref, inView } = useInView<HTMLLIElement>();
  const { url } = useBlobUrl(inView && album.coverPhotoId ? album.coverPhotoId : '', () =>
    photoThumbnailLoader(organization.id)({ id: album.coverPhotoId! }),
  );

  return (
    <li ref={ref} className="group relative">
      <Link
        to="/organizations/$organizationId/photos/albums/$albumId"
        params={{ organizationId: organization.id, albumId: album.id }}
        className="flex flex-col overflow-hidden rounded-lg border transition-colors hover:bg-muted/50"
      >
        <span className="flex aspect-square w-full items-center justify-center bg-muted/60">
          {url ? (
            <img src={url} alt="" loading="lazy" className="size-full object-cover" />
          ) : (
            <Images className="size-8 text-muted-foreground" strokeWidth={1.5} />
          )}
        </span>
        <span className="flex flex-col gap-0.5 p-2">
          <span className="truncate text-sm font-medium">{album.name}</span>
          <span className="text-xs text-muted-foreground">
            {t('albums.count', { count: album.photoCount })}
          </span>
        </span>
      </Link>

      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          aria-label={t('albums.rename')}
          onClick={onRename}
        >
          <Pencil />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          aria-label={t('albums.remove')}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>
    </li>
  );
}

export function AlbumDialog({
  organization,
  album,
  onClose,
}: {
  organization: Organization;
  album?: Album;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(album?.name ?? '');
  const create = useCreateAlbum();
  const rename = useRenameAlbum();
  const mutation = album ? rename : create;

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (album) {
      rename.mutate(
        { organizationId: organization.id, albumId: album.id, name: trimmed },
        { onSuccess: onClose },
      );
      return;
    }

    create.mutate({ organizationId: organization.id, name: trimmed }, { onSuccess: onClose });
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{album ? t('albums.rename') : t('albums.create')}</DialogTitle>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor="album-name">{t('albums.nameLabel')}</FieldLabel>
          <Input
            id="album-name"
            autoFocus
            maxLength={80}
            value={name}
            placeholder={t('albums.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
          />
        </Field>

        <ErrorNote error={mutation.error} />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending ? <LoaderCircle className="size-4 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
